import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PriorityBadge, useSd, errText } from "@/components/servicedesk/SdBits";
import { NewsBoard } from "@/components/servicedesk/SdNews";
import { useAuth } from "@/context/AuthContext";

const Pick = ({ label, value, onChange, options, testid }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Select…" /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const Hints = ({ kb, dups }) => (!kb.length && !dups.length) ? null : (
  <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 space-y-3" data-testid="sd-hints">
    {!!kb.length && (<div><div className="text-xs font-semibold text-sky-900 mb-1 flex items-center gap-1"><Icons.BookOpen className="h-3.5 w-3.5" />These guides may fix it right away</div>
      {kb.map((a) => <Link key={a.id} to={`/servicedesk/kb?id=${a.id}`} target="_blank" className="block text-sm text-sky-700 hover:underline" data-testid={`sd-kb-hint-${a.id}`}>{a.title}</Link>)}</div>)}
    {!!dups.length && (<div><div className="text-xs font-semibold text-amber-900 mb-1 flex items-center gap-1"><Icons.Copy className="h-3.5 w-3.5" />Similar open tickets in your company</div>
      {dups.map((d) => <Link key={d.id} to={`/servicedesk/tickets/${d.id}`} className="block text-sm text-amber-800 hover:underline" data-testid={`sd-dup-${d.number}`}>{d.number} · {d.subject} · {d.status}{d.is_major ? " · MAJOR INCIDENT" : ""}</Link>)}</div>)}
  </div>
);

const MAX_MB = 2;
const EMPTY = { type: "Incident", category: "", subcategory: "", subject: "", description: "", location: "", contact: "", asset: "", impact: "Medium", urgency: "Medium", preferred_schedule: "", justification: "", company_id: "" };

export default function SdSubmit() {
  const nav = useNavigate();
  const { cat, me } = useSd();
  const { user } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [companies, setCompanies] = useState([]);
  useEffect(() => { api.get("/servicedesk/companies").then(({ data }) => setCompanies(data)).catch(() => {}); }, []);
  useEffect(() => {
    const own = user?.active_company_id || user?.company_id;
    if (own && companies.some((c) => c.id === own)) setF((x) => ({ ...x, company_id: x.company_id || own }));
  }, [user, companies]);
  useEffect(() => {
    const e = me?.employee;
    if (e) setF((x) => ({ ...x, location: x.location || e.site_name || e.work_location || "", contact: x.contact || e.mobile || "" }));
  }, [me]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [kb, setKb] = useState([]);
  const [dups, setDups] = useState([]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const subs = useMemo(() => cat?.categories.find((c) => c.name === f.category)?.subcategories || [], [cat, f.category]);
  const needsAppr = cat?.approval_types.includes(f.type);
  const pr = f.type === "Security Incident" && f.impact !== "Low" ? "P1" : cat?.matrix[f.impact]?.[f.urgency];

  useEffect(() => {
    const q = `${f.subject} ${f.description}`.trim();
    const t = setTimeout(() => {
      api.get("/servicedesk/kb/suggest", { params: { q } }).then(({ data }) => setKb(data)).catch(() => {});
      if (f.subject.length > 5) api.get("/servicedesk/duplicates", { params: { subject: f.subject, category: f.category } }).then(({ data }) => setDups(data)).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [f.subject, f.description, f.category]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company_id) { toast.error("Please select the company"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/servicedesk/tickets", f);
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/servicedesk/tickets/${data.id}/attachments`, fd).catch(() => toast.error(`Could not upload ${file.name}`));
      }
      toast.success(`Ticket ${data.number} submitted`);
      nav(`/servicedesk/tickets/${data.id}`);
    } catch (err) { toast.error(errText(err, "Could not submit")); }
    setBusy(false);
  };

  if (!cat) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  return (
    <div data-testid="sd-submit-page">
      <PageHeader title="Submit a Ticket" subtitle="Tell IT what's wrong or what you need. You'll get a ticket number right away." />
      <div className="grid 2xl:grid-cols-[minmax(0,56rem)_minmax(0,1fr)] gap-6 items-start">
      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_280px] gap-5 max-w-4xl">
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Pick label="Ticket type" value={f.type} onChange={set("type")} options={cat.types} testid="sd-type" />
            <Pick label="Category" value={f.category} onChange={(v) => setF((x) => ({ ...x, category: v, subcategory: "" }))} options={cat.categories.map((c) => c.name)} testid="sd-category" />
            <Pick label="Issue / subcategory" value={f.subcategory} onChange={set("subcategory")} options={subs} testid="sd-subcategory" />
            <div className="space-y-1.5"><Label className="text-xs">Location *</Label><Input value={f.location} onChange={set("location")} placeholder="Office, floor or branch" data-testid="sd-location" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Subject *</Label><Input value={f.subject} onChange={set("subject")} placeholder="e.g. Laptop does not turn on" data-testid="sd-subject" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Description *</Label><Textarea rows={5} value={f.description} onChange={set("description")} placeholder="What happened, since when, any error message" data-testid="sd-description" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Company *</Label>
            <Select value={f.company_id} onValueChange={set("company_id")}>
              <SelectTrigger data-testid="sd-company"><SelectValue placeholder="Select the company this ticket is for" /></SelectTrigger>
              <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`sd-company-opt-${c.code || c.id}`}>{c.name}</SelectItem>)}</SelectContent>
            </Select></div>
          {needsAppr && <div className="space-y-1.5"><Label className="text-xs">Business justification *</Label><Textarea rows={3} value={f.justification} onChange={set("justification")} placeholder="Why it is needed, specifications, budget" data-testid="sd-justification" /></div>}
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label className="text-xs">Affected asset</Label><Input value={f.asset} onChange={set("asset")} placeholder="Asset tag or model" data-testid="sd-asset" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Contact number</Label><Input value={f.contact} onChange={set("contact")} data-testid="sd-contact" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Preferred schedule</Label><Input value={f.preferred_schedule} onChange={set("preferred_schedule")} placeholder="e.g. after 3 PM" data-testid="sd-schedule" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Attachments (max 2 MB each)</Label><Input type="file" multiple onChange={(e) => {
            const all = [...e.target.files]; const big = all.filter((x) => x.size > MAX_MB * 1024 * 1024);
            if (big.length) { toast.error(`Over 2 MB, not attached: ${big.map((x) => x.name).join(", ")}`); e.target.value = ""; }
            setFiles(big.length ? [] : all);
          }} data-testid="sd-files" /></div>
        </div>
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
            <Pick label="Business impact" value={f.impact} onChange={set("impact")} options={cat.levels} testid="sd-impact" />
            <Pick label="Urgency" value={f.urgency} onChange={set("urgency")} options={cat.levels} testid="sd-urgency" />
            <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600" data-testid="sd-priority-preview">
              <div className="flex items-center gap-2 mb-1">Suggested priority <PriorityBadge p={pr} /></div>
              Respond within {cat.sla[pr]?.[0]}h · resolve within {cat.sla[pr]?.[1]}h (business hours). IT may adjust the priority.
              {needsAppr && <div className="mt-2 text-violet-700">This request needs approval before IT starts.</div>}
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="sd-submit-btn">{busy ? "Submitting…" : "Submit ticket"}</Button>
          </div>
          <Hints kb={kb} dups={dups} />
        </div>
      </form>
      <NewsBoard />
      </div>
    </div>
  );
}
